import React, { createContext, useContext, useMemo } from 'react';
import { STATIC_TOOL_CONFIGS } from '../content/tool-configs';
import { Features } from '../types/features';

type Branding = {
  displayName?: string;
  logoUrl?: string;
};

type AccountManager = {
  name?: string;
  email?: string;
};

export interface DocumentGuidance {
  title: string;
  shortDescription: string;
  acceptedFormats: string[];
  recommendedDocuments: string[];
  examples?: string[];
  tips?: string[];
}

export interface ToolConfig {
  description: string;
  documentGuidance: DocumentGuidance;
}

export type TenantConfig = {
  organizationId: string;
  enabledFeatures: string[];
  branding: Branding;
  accountManager?: AccountManager;
  toolConfigs?: Record<string, ToolConfig>;
};

type TenantContextValue = {
  loading: boolean;
  error?: string;
  config?: TenantConfig;
  isFeatureEnabled: (feature: string) => boolean;
};

const TenantConfigContext = createContext<TenantContextValue>({
  loading: true,
  isFeatureEnabled: () => true,
});

export const useTenantConfig = () => useContext(TenantConfigContext);

export const TenantConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const config = useMemo<TenantConfig>(() => ({
    organizationId: import.meta.env.VITE_TENANT_ORG_ID || 'raf',
    enabledFeatures: Object.values(Features),
    branding: {
      displayName: import.meta.env.VITE_BRAND_NAME || 'RAF',
      logoUrl: import.meta.env.VITE_BRAND_LOGO_PATH || '/brand/app-icon.svg',
    },
    accountManager: {
      name: import.meta.env.VITE_SUPPORT_NAME || 'RAF Support',
      email: import.meta.env.VITE_SUPPORT_EMAIL || 'support@raf.local',
    },
    toolConfigs: STATIC_TOOL_CONFIGS,
  }), []);

  const isFeatureEnabled = useMemo(() => {
    const features = new Set((config?.enabledFeatures || []).map((s) => String(s).toUpperCase()));
    return (feature: string) => features.has(String(feature).toUpperCase());
  }, [config?.enabledFeatures]);

  const value: TenantContextValue = { loading: false, config, isFeatureEnabled };

  return (
    <TenantConfigContext.Provider value={value}>{children}</TenantConfigContext.Provider>
  );
};


